/**
 * Small, dependency-free helpers for turning raw FHIR JSON into
 * human-readable strings/labels. Intentionally generic so the Admin panel
 * can render any resource type without a per-type component.
 */

/** A minimal shape for the raw FHIR resources the panel handles. */
export interface FhirResource {
  resourceType: string;
  id?: string;
  meta?: {
    versionId?: string;
    lastUpdated?: string;
    tag?: { system?: string; code?: string; display?: string }[];
  };
  [key: string]: unknown;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * True when a value renders cleanly on one line: a primitive, or a well-known
 * FHIR datatype (CodeableConcept, Coding, Quantity, Reference, Period,
 * HumanName). Complex objects/arrays return false so callers can show the full
 * structure instead of a truncated one-liner.
 */
export const isSimpleFhirValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value !== "object") return true;
  if (Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    "text" in v ||
    "coding" in v ||
    "reference" in v ||
    "display" in v ||
    ("value" in v && ("unit" in v || "code" in v)) ||
    "start" in v ||
    "end" in v ||
    "family" in v ||
    "given" in v
  );
};

/**
 * Best-effort human-readable rendering of a FHIR value node
 * (CodeableConcept, Coding, Quantity, Reference, Period, primitives, ...).
 */
export const formatFhirValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);

  if (Array.isArray(value)) {
    return value.map(formatFhirValue).filter(Boolean).join(", ");
  }

  if (isRecord(value)) {
    // CodeableConcept
    if ("text" in value && typeof value.text === "string") return value.text;
    if (Array.isArray(value.coding) && value.coding.length > 0) {
      return formatFhirValue(value.coding[0]);
    }
    // Reference (checked before bare display/code so the id is preserved)
    if ("reference" in value && typeof value.reference === "string") {
      return typeof value.display === "string"
        ? `${value.display} (${value.reference})`
        : value.reference;
    }
    // Coding
    if ("display" in value && typeof value.display === "string")
      return value.display;
    if ("code" in value && typeof value.code === "string") {
      const system =
        typeof value.system === "string"
          ? ` (${value.system.split("/").pop()})`
          : "";
      return `${value.code}${system}`;
    }
    // Quantity
    if ("value" in value && (value.unit || value.code)) {
      return `${value.value} ${value.unit ?? value.code ?? ""}`.trim();
    }
    // Period
    if ("start" in value || "end" in value) {
      return `${value.start ?? "?"} – ${value.end ?? "ongoing"}`;
    }
    // HumanName
    if ("family" in value || "given" in value) {
      const given = Array.isArray(value.given) ? value.given.join(" ") : "";
      return `${given} ${value.family ?? ""}`.trim();
    }
  }

  // Fallback: compact JSON, truncated.
  const json = JSON.stringify(value);
  return json.length > 120 ? `${json.slice(0, 117)}…` : json;
};

/** Curated friendly labels for common FHIR leaf fields (clinical view). */
const FIELD_LABELS: Record<string, string> = {
  status: "Status",
  clinicalStatus: "Clinical status",
  verificationStatus: "Verification status",
  code: "Code",
  category: "Category",
  note: "Note",
  onsetDateTime: "Onset",
  abatementDateTime: "Resolved",
  recordedDate: "Recorded date",
  effectiveDateTime: "Effective date",
  valueQuantity: "Value",
  valueString: "Value",
  dosageInstruction: "Dosage",
  medicationCodeableConcept: "Medication",
  reasonCode: "Reason",
  period: "Period",
  class: "Class",
  type: "Type",
  priority: "Priority",
  intent: "Intent",
  subject: "Patient",
  performer: "Performer",
  requester: "Requester",
  authoredOn: "Authored on",
  text: "Text",
  display: "Display",
  quantity: "Quantity",
  doseQuantity: "Dose",
  vaccineCode: "Vaccine",
  bodySite: "Body site",
  severity: "Severity",
};

const prettifySegment = (segment: string): string => {
  const label = FIELD_LABELS[segment];
  if (label) return label;
  // camelCase / snake_case → Title Case words.
  return segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
};

/**
 * Turn a structured change path into a human-friendly label.
 * `["dosageInstruction", 0, "text"]` → "Dosage › #1 › Text".
 */
export const humanizePath = (path: (string | number)[]): string => {
  if (path.length === 0) return "Resource";
  return path
    .map((segment) =>
      typeof segment === "number"
        ? `#${segment + 1}`
        : prettifySegment(segment),
    )
    .join(" › ");
};

/** Read the `created-source` meta tag (which service first created the resource). */
export const getCreatedSource = (resource?: FhirResource): string | undefined =>
  resource?.meta?.tag?.find((t) => t.system?.includes("created-source"))?.code;
